import { memo, useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "../api";
import { useAuth } from "../AuthContext";
import { Send, Image as ImageIcon, Video, Shield, Flag, Lock, LogOut, Camera, Eye, X, CheckCircle2, ShieldAlert, Swords, Key, Check } from "lucide-react";
import { toast } from "sonner";
import ZoomV2 from "../components/match/ZoomV2";
import ScopeReportPanel from "../components/match/ScopeReportPanel";
import ChatMessage from "../components/match/ChatMessage";
import LiveStreamsPanel from "../components/match/LiveStreamsPanel";
import MatchPrayerBreak from "../components/match/MatchPrayerBreak";
import H2HWidget from "../components/match/H2HWidget";
import RivalsGuardPanel from "../components/match/RivalsGuardPanel";

const IMG_MAX = 3_000_000;
const CHAT_POLL_MS = 5000;
const MATCH_POLL_MS = 12000;

function matchWithClock(data, previousOffset) {
  const serverTime = Date.parse(data?.server_time);
  const measuredOffset = Number.isFinite(serverTime) ? serverTime - Date.now() : 0;
  const offset = Number.isFinite(previousOffset) && Math.abs(measuredOffset - previousOffset) < 2000
    ? previousOffset : measuredOffset;
  return { ...data, serverClockOffsetMs: offset };
}

function readAsDataURL(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

async function uploadVideo(file, onProgress) {
  const form = new FormData();
  form.append("file", file);
  const { data } = await api.post("/upload/video", form, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: (e) => {
      if (e.total && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
    },
    timeout: 0,
    maxContentLength: Infinity,
    maxBodyLength: Infinity,
  });
  return data.url;
}

// ==============================================================================
// 🎮 مكون كود البارتي الدائم
// ==============================================================================
function PartyCodeSection({ match, matchId, onRefresh, isStaffOfMatch, userClanId }) {
  const [code, setCode] = useState(match?.party_code || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setCode(match?.party_code || "");
  }, [match?.party_code]);

  const isMatchParticipantOrAdmin = useMemo(() => {
    if (!match) return false;
    const isStaff = userClanId === match.clan_a_id || userClanId === match.clan_b_id;
    return isStaff || isStaffOfMatch;
  }, [match, userClanId, isStaffOfMatch]);

  if (!isMatchParticipantOrAdmin) return null;

  const handleSave = async (e) => {
    e.preventDefault();
    const clean = code.trim().toUpperCase();
    if (clean.length !== 5) {
      return toast.error("يجب أن يكون كود البارتي مكوناً من 5 خانات/أحرف");
    }
    setSaving(true);
    try {
      await api.post(`/matches/${matchId}/party-code`, { party_code: clean });
      toast.success("تم حفظ كود البارتي بنجاح!");
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "تعذر حفظ كود البارتي");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex justify-center w-full my-4">
      <div className="bg-[#111113]/90 border border-white/10 rounded-2xl p-4 w-full max-w-xl flex items-center justify-between gap-4 shadow-xl">
        <div className="flex items-center gap-2">
          <Key className="text-gold-500" size={18} />
          <span className="text-xs font-black text-white uppercase tracking-widest">
            كود البارتي الدائم (Party Code):
          </span>
        </div>
        <form onSubmit={handleSave} className="flex items-center gap-2">
          <input
            type="text"
            maxLength={5}
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="5 خانات"
            disabled={!isStaffOfMatch}
            className="w-32 bg-black/60 border border-white/15 rounded-xl px-3 py-1.5 text-center font-mono font-black text-gold-400 text-sm tracking-widest outline-none focus:border-gold-500/50 transition disabled:opacity-60 shadow-inner"
          />
          {isStaffOfMatch && (
            <button
              type="submit"
              disabled={saving}
              className="bg-gold-500 hover:bg-gold-400 text-black px-4 py-1.5 rounded-xl text-xs font-black transition disabled:opacity-50 shadow-md"
            >
              {saving ? "..." : "حفظ"}
            </button>
          )}
        </form>
      </div>
    </div>
  );
}

// ==============================================================================
// 🛡️ مكون اختيار وتعديل لاعبي الـ Counter UAV
// ==============================================================================
function CounterUAVSelectorPanel({ match, userClanId, onRefresh }) {
  const { user } = useAuth();
  const [clans, setClans] = useState({});
  const [rosterError, setRosterError] = useState(false);
  const [retry, setRetry] = useState(0);
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);
  const busy = useRef(false);
  const maps = match.maps || [];
  const index = maps.findIndex((m, i) => !m.winner && !m.winner_clan_id && !m.admin_resolved && !match[`map_${i + 1}_winner_clan_id`]);
  const active = match.status === "live" && index >= 0;
  const number = index + 1;
  const mySide = user?.clan_id === match.clan_a_id ? "a" : user?.clan_id === match.clan_b_id ? "b" : null;
  const myClan = mySide ? clans[mySide] : null;
  const canEdit = active && !!user?.id && !!myClan && [myClan.leader_id, ...(myClan.vice_leader_ids || [])].includes(user.id);
  const selections = active ? (maps[index].counter_uav || {}) : {};

  useEffect(() => {
    const controller = new AbortController();
    setClans({});
    setRosterError(false);
    Promise.all([
      api.get(`/clans/${match.clan_a_id}`, { signal: controller.signal, timeout: 10000 }),
      api.get(`/clans/${match.clan_b_id}`, { signal: controller.signal, timeout: 10000 })
    ]).then(([a, b]) => {
      if (!controller.signal.aborted) setClans({ a: a.data, b: b.data });
    }).catch(() => { if (!controller.signal.aborted) setRosterError(true); });
    return () => controller.abort();
  }, [match.clan_a_id, match.clan_b_id, number, retry]);
  useEffect(() => { setAdding(false); }, [match.id, number, active]);

  const idsFor = side => selections[side]?.player_ids || [];
  const save = async (ids) => {
    if (!canEdit || busy.current || ids.length > 2) return;
    busy.current = true;
    setSaving(true);
    try {
      await api.post(`/matches/${match.id}/counter-uav`, {
        map_number: number, side: mySide, selected_player_ids: ids, is_locked: false
      });
      setAdding(false);
      await onRefresh?.();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || "تعذر حفظ الاختيار");
      await onRefresh?.();
    } finally {
      busy.current = false;
      setSaving(false);
    }
  };

  return (
    <div className="bg-[#111113]/95 border border-gold-500/30 rounded-2xl p-4 shadow-2xl space-y-4">
      <div className="text-xs font-black text-gold-400">Counter UAV {active ? `• ماب ${number}` : "• المباراة منتهية"}</div>
      <p className="text-xs text-white/50">يختار القائد أو النواب لاعبين كحد أقصى من أعضاء كلانهم. كل اسم يثبت فور اختياره ولا يمكن حذفه أو تبديله حتى حسم الماب، ثم يُصفّر الاختيار.</p>
      {rosterError && <button className="text-xs text-amber-400 underline" onClick={() => setRetry(n => n + 1)}>تعذر تحميل الأعضاء — إعادة المحاولة</button>}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {["a", "b"].map(side => {
          const roster = clans[side]?.members || [];
          const ids = idsFor(side);
          const names = selections[side]?.players || [];
          const own = canEdit && mySide === side;
          return <div key={side} className="bg-black/40 p-3 rounded-xl border border-white/10 space-y-3">
            <div className="text-xs font-bold text-gold-400">{match[`clan_${side}`]?.name || clans[side]?.name || "الكلان"} • {ids.length}/2</div>
            <div className="flex flex-wrap gap-2">
              {ids.map((id, i) => <span key={id} className="flex items-center gap-2 rounded-lg border border-gold-500/40 px-3 py-2 text-xs text-gold-300">
                {roster.find(p => p.id === id)?.username || names[i] || id}
                <Lock size={12} aria-label="الاختيار ثابت حتى نهاية الماب" />
              </span>)}
              {!ids.length && <span className="text-xs text-white/40">لم يتم اختيار لاعبين</span>}
            </div>
            {own && <button type="button" disabled={saving || ids.length >= 2} onClick={() => setAdding(value => !value)} className="rounded-lg bg-gold-500 px-3 py-2 text-xs font-bold text-black disabled:opacity-40">{saving ? "جاري الحفظ..." : "إضافة لاعب"}</button>}
            {own && adding && ids.length < 2 && <select aria-label="اختيار لاعب Counter UAV" value="" disabled={saving} onChange={e => { if (e.target.value) save([...ids, e.target.value]); }} className="block w-full rounded-lg bg-black border border-white/20 p-2 text-sm text-white">
              <option value="">اختر لاعبًا من الكلان</option>
              {roster.filter(p => !ids.includes(p.id)).map(p => <option key={p.id} value={p.id}>{p.username || p.act || p.id}</option>)}
            </select>}
          </div>;
        })}
      </div>
    </div>
  );
}


// ==============================================================================
// 📸 مكون الماب (مع حصر الـ Veto والمهلة وحساب الفوز عند الصفر بدقة)
// ==============================================================================
export function MapCard({ mapNumber, match, isAdmin, isLeaderA, isLeaderB, userClanId, onRefresh, onGrace, graceBusy, activeMapNumber, onStageCorrection, correctionEnabled, stagedCorrection }) {
  const [uploading, setUploading] = useState(false);
  const [roundScoreA, setRoundScoreA] = useState("");
  const [roundScoreB, setRoundScoreB] = useState("");
  const [modalImage, setModalImage] = useState(null);
  const [mapNameInput, setMapNameInput] = useState("");

  const mapData = match.maps?.[mapNumber - 1] || {};
  const proofUrl = match[`map_${mapNumber}_proof`] || mapData.proof_url || null;
  const winnerClanId = match[`map_${mapNumber}_winner_clan_id`] || mapData.winner_clan_id || (mapData.winner === "A" ? match.clan_a_id : mapData.winner === "B" ? match.clan_b_id : null);
  const isMapFinished = !!winnerClanId;
  useEffect(() => {
    setRoundScoreA(mapData.round_score_a == null ? "" : String(mapData.round_score_a));
    setRoundScoreB(mapData.round_score_b == null ? "" : String(mapData.round_score_b));
  }, [mapData.round_score_a, mapData.round_score_b]);

  const isCurrentActiveMap = mapNumber === activeMapNumber;

  const clanAId = match?.clan_a_id || match?.clan_a?.id;
  const clanBId = match?.clan_b_id || match?.clan_b?.id;
  const clanAName = match?.clan_a?.name || match?.clan_a?.tag || "الكلان الأول";
  const clanBName = match?.clan_b?.name || match?.clan_b?.tag || "الكلان الثاني";
  
  const expectedChoosingClanId = mapNumber % 2 !== 0 ? clanAId : clanBId;
  const isMyClanTurn = userClanId === expectedChoosingClanId;
  const choosingClanName = expectedChoosingClanId === clanAId ? clanAName : clanBName;

  const graceState = mapData.grace_state || {};
  const [timeLeft, setTimeLeft] = useState(0);

  // Display only: the server decides the winner, even with every browser closed.
  useEffect(() => {
    if (!graceState.active || !graceState.ends_at || graceState.paused || isMapFinished) {
      setTimeLeft(0);
      return;
    }
    let lastRefresh = 0;
    const calculateTime = () => {
      const remaining = new Date(graceState.ends_at).getTime() - (Date.now() + (match.serverClockOffsetMs || 0));
      setTimeLeft(Math.max(0, Math.ceil(remaining / 1000)));
      if (remaining <= 0 && document.visibilityState === "visible" && Date.now() - lastRefresh >= 3000) {
        lastRefresh = Date.now();
        onRefresh?.();
      }
    };
    calculateTime();
    const interval = setInterval(calculateTime, 1000);
    return () => clearInterval(interval);
  }, [graceState.active, graceState.ends_at, graceState.paused, isMapFinished, match.serverClockOffsetMs, onRefresh]);

  const formatTimer = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins < 10 ? "0" : ""}${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  const handleSelectMapName = async (e) => {
    e.preventDefault();
    if (!mapNameInput.trim()) return;

    if (!isMyClanTurn && !isAdmin) {
      return toast.error(`عذراً، هذا الماب مخصص لـ ${choosingClanName} فقط لاختياره!`);
    }

    try {
      await api.post(`/matches/${match.id}/map-veto`, {
        map_number: mapNumber,
        map_name: mapNameInput.trim()
      });
      toast.success(`تم اختيار ماب ${mapNumber} بنجاح!`);
      setMapNameInput("");
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "تعذر اختيار الماب");
    }
  };

  const handleUploadProof = async (e) => {
    if (match.status !== "live" || !isCurrentActiveMap) { toast.error("هذا الماب مقفل أو لم يحن دوره"); return; }
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > IMG_MAX) return toast.error("حجم الصورة كبير جداً (الحد 3MB)");
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) return toast.error("اختر صورة JPG أو PNG أو WebP");

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      await api.post(`/matches/${match.id}/upload-proof?map=${mapNumber}`, formData);
      toast.success(`تم رفع إثبات الماب ${mapNumber} بنجاح!`);
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "حدث خطأ أثناء رفع صورة الإثبات");
    } finally {
      setUploading(false);
    }
  };

  const scorePayload = (chosenClanId) => {
    const a = Number(roundScoreA), b = Number(roundScoreB);
    if (roundScoreA === "" || roundScoreB === "" || !Number.isInteger(a) || !Number.isInteger(b) || a < 0 || b < 0) { toast.error("اكتب نتيجة الماب أولاً مثل 8-7"); return null; }
    if ((chosenClanId === match.clan_a?.id && a <= b) || (chosenClanId === match.clan_b?.id && b <= a)) { toast.error("الفائز المختار يجب أن تكون نتيجته أعلى"); return null; }
    return { score_a: a, score_b: b };
  };

  const handleVoteWinner = async (chosenClanId) => {
    if (match.status !== "live" || !isCurrentActiveMap) { toast.error("هذا الماب مقفل أو لم يحن دوره"); return; }
    const scores = scorePayload(chosenClanId); if (!scores) return;
    try {
      await api.post(`/matches/${match.id}/vote-map`, {
        map_index: mapNumber - 1,
        winner_clan_id: chosenClanId,
        ...scores
      });
      toast.success("تم إرسال صوتك بنجاح!");
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "تعذر تسجيل التصويت");
    }
  };

  const handleAdminResolve = async (chosenClanId) => {
    if (match.status === "finished") {
      if (!correctionEnabled) return toast.error("هذا الماب غير مطلوب بعد حسم السلسلة");
      const scores = scorePayload(chosenClanId); if (!scores) return;
      onStageCorrection?.({map_index: mapNumber - 1, winner_clan_id: chosenClanId, ...scores});
      return;
    }
    if (match.status !== "live" || (!isCurrentActiveMap && !isMapFinished)) { toast.error("هذا الماب مقفل أو لم يحن دوره"); return; }
    const scores = scorePayload(chosenClanId); if (!scores) return;
    try {
      await api.post(`/matches/${match.id}/admin-resolve-map`, {
        map_index: mapNumber - 1,
        winner_clan_id: chosenClanId,
        ...scores
      });
      toast.success("تم حسم الماب بواسطة المنظم بنجاح!");
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "تعذر حسم الماب");
    }
  };

  return (
    <>
      <div className="bg-[#111113]/90 border border-white/10 rounded-2xl p-4 flex flex-col justify-between relative overflow-hidden shadow-xl hover:border-gold-500/30 transition">
        <div className="flex justify-between items-center mb-2">
          <span className="text-xs font-black text-white/50 uppercase tracking-widest">
            ماب {mapNumber}
          </span>
          {isMapFinished && (
            <span className="text-[10px] bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 px-2 py-0.5 rounded-full font-bold flex items-center gap-1">
              <CheckCircle2 size={11} /> مكتمل
            </span>
          )}
        </div>

        {graceState.active && !isMapFinished && (
          <div className="my-2 bg-destructive/15 border border-destructive/40 rounded-xl p-2.5 flex items-center justify-between text-xs animate-pulse">
            <span className="text-destructive font-bold flex items-center gap-1">
              <ShieldAlert size={14} /> مهلة بدأها {graceState.started_by === "A" ? clanAName : clanBName}:
            </span>
            <span className="font-mono font-black text-white bg-destructive px-2 py-0.5 rounded-lg">
              {graceState.paused ? "متوقف مؤقتاً (بريك)" : formatTimer(timeLeft)}
            </span>
          </div>
        )}

        <div className="mb-3">
          {mapData.map_name ? (
            <div className="bg-black/50 border border-gold-500/30 rounded-xl px-3 py-1.5 text-center text-xs font-bold text-gold-400">
              🗺️ الماب: {mapData.map_name}
            </div>
          ) : (
            <div>
              {match.status === "live" && ((isMyClanTurn && isCurrentActiveMap) || isAdmin) ? (
                <form onSubmit={handleSelectMapName} className="flex gap-1.5">
                  <input
                    type="text"
                    value={mapNameInput}
                    onChange={(e) => setMapNameInput(e.target.value)}
                    placeholder={`دور ${choosingClanName} للاختيار...`}
                    className="flex-1 bg-black/60 border border-gold-500/40 rounded-xl px-3 py-1.5 text-xs text-white placeholder:text-white/30 outline-none focus:border-gold-500"
                  />
                  <button type="submit" className="bg-gold-500 text-black font-black px-3 py-1.5 rounded-xl text-xs hover:bg-gold-400 transition">
                    اختر
                  </button>
                </form>
              ) : (
                <div className="text-[11px] text-white/40 text-center bg-black/30 border border-white/5 rounded-xl py-2 font-bold">
                  🔒 دور {choosingClanName} للاختيار
                </div>
              )}
            </div>
          )}
        </div>

        {!proofUrl && isMapFinished ? (
          <div className="my-2 bg-black/90 border border-gold-500/50 px-4 py-2 rounded-xl text-center">
            <div className="text-[9px] text-gold-500 font-bold">الفائز بالماب</div>
            <div className="text-base font-black text-white">{winnerClanId === clanAId ? clanAName : clanBName}</div>
          </div>
        ) : !proofUrl ? (
          <div className="py-6 my-2 border-2 border-dashed border-white/10 rounded-xl flex flex-col items-center justify-center gap-2 bg-black/30">
            <Camera className="text-white/30" size={24} />
            <label className="cursor-pointer bg-gold-500/10 hover:bg-gold-500/20 text-gold-500 border border-gold-500/30 text-xs px-4 py-2 rounded-xl font-black transition">
              {uploading ? "جاري الرفع..." : "📸 رفع صورة النتيجة أولاً"}
              <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleUploadProof} disabled={uploading} />
            </label>
          </div>
        ) : (
          <div className="relative my-2 group rounded-xl overflow-hidden border border-white/15 bg-black">
            <div className="h-36 w-full relative cursor-pointer" onClick={() => setModalImage(proofUrl)}>
              <img src={proofUrl} alt={`إثبات ماب ${mapNumber}`} className="w-full h-full object-cover opacity-60" />
            </div>
            <div className="absolute inset-0 flex flex-col items-center justify-center p-3 text-center pointer-events-none">
              {!isMapFinished ? (
                <div className="pointer-events-auto space-y-1.5 w-full max-w-[200px]">
                  <div className="grid grid-cols-2 gap-1 mb-2">
                    <label className="text-[9px] text-white/60">{clanAName}<input type="number" min="0" max="99" value={roundScoreA} onChange={e=>setRoundScoreA(e.target.value)} className="mt-1 w-full bg-black/80 border border-white/20 rounded px-2 py-1 text-center text-white" placeholder="8" /></label>
                    <label className="text-[9px] text-white/60">{clanBName}<input type="number" min="0" max="99" value={roundScoreB} onChange={e=>setRoundScoreB(e.target.value)} className="mt-1 w-full bg-black/80 border border-white/20 rounded px-2 py-1 text-center text-white" placeholder="7" /></label>
                  </div>
                  <p className="text-[10px] font-bold text-white mb-1">تصويت الكلانين على نتيجة الماب:</p>
                  <button
                    onClick={() => handleVoteWinner(match.clan_a?.id)}
                    className="w-full bg-black/85 hover:bg-gold-500 hover:text-black text-white text-xs py-1.5 rounded-lg border border-white/20 font-black transition"
                  >
                    صوت لـ {clanAName}
                  </button>
                  <button
                    onClick={() => handleVoteWinner(match.clan_b?.id)}
                    className="w-full bg-black/85 hover:bg-gold-500 hover:text-black text-white text-xs py-1.5 rounded-lg border border-white/20 font-black transition"
                  >
                    صوت لـ {clanBName}
                  </button>
                </div>
              ) : (
                <div className="bg-black/90 border border-gold-500/50 px-4 py-2 rounded-xl text-center shadow-2xl">
                  <div className="text-[9px] uppercase tracking-widest text-gold-500 font-bold">الفائز بالماب</div>
                  <div className="text-base font-black text-white">
                    {winnerClanId === clanAId ? clanAName : clanBName}
                  </div>
                  {mapData.round_score_a != null && mapData.round_score_b != null && <div className="text-xs text-white/60 mt-1">{clanAName} {mapData.round_score_a} - {mapData.round_score_b} {clanBName}</div>}
                </div>
              )}
            </div>
          </div>
        )}

        {mapData.disputed && !isMapFinished && (
          <div className="bg-destructive/25 text-destructive text-[11px] font-bold p-2 rounded-xl text-center my-2">
            ⚠️ اختلاف في التصويت! الماب في حالة نزاع.
          </div>
        )}

        {isAdmin && (
          <div className="mt-2 pt-2 border-t border-white/10">
            <div className="text-[10px] text-gold-500 font-bold mb-1 flex items-center justify-between">
              <span>قرار المنظم الفاصل:</span>
            </div>
            <div className="grid grid-cols-2 gap-2 mb-2">
              <label className="text-xs text-white/70">{clanAName}<input aria-label={`راوندات ${clanAName} ماب ${mapNumber}`} type="number" min="0" max="99" step="1" value={roundScoreA} onChange={e=>setRoundScoreA(e.target.value)} disabled={match.status === "finished" && !correctionEnabled} className="mt-1 w-full bg-black border border-white/20 rounded-lg p-2 text-center text-white" /></label>
              <label className="text-xs text-white/70">{clanBName}<input aria-label={`راوندات ${clanBName} ماب ${mapNumber}`} type="number" min="0" max="99" step="1" value={roundScoreB} onChange={e=>setRoundScoreB(e.target.value)} disabled={match.status === "finished" && !correctionEnabled} className="mt-1 w-full bg-black border border-white/20 rounded-lg p-2 text-center text-white" /></label>
            </div>
            {stagedCorrection && <p className="text-xs text-gold-400 mb-2">مضاف للتصحيح: {clanAName} {stagedCorrection.score_a} — {stagedCorrection.score_b} {clanBName}</p>}
            <div className="grid grid-cols-2 gap-1.5">
              <button
                disabled={match.status === "finished" && !correctionEnabled}
                onClick={() => handleAdminResolve(match.clan_a?.id)}
                className="text-[11px] py-1.5 rounded-lg font-bold border bg-gold-500/10 text-gold-400 border-gold-500/30 hover:bg-gold-500/20"
              >
                حسم لـ {clanAName}
              </button>
              <button
                disabled={match.status === "finished" && !correctionEnabled}
                onClick={() => handleAdminResolve(match.clan_b?.id)}
                className="text-[11px] py-1.5 rounded-lg font-bold border bg-gold-500/10 text-gold-400 border-gold-500/30 hover:bg-gold-500/20"
              >
                حسم لـ {clanBName}
              </button>
            </div>
          </div>
        )}

        {!proofUrl && !isMapFinished && isCurrentActiveMap && ((graceState.active && (isAdmin || (graceState.started_by === "A" ? isLeaderA : isLeaderB))) || (!graceState.active && (isLeaderA || isLeaderB))) && (
          <button
            disabled={graceBusy}
            onClick={() => onGrace && onGrace(mapNumber)}
            className={`w-full mt-2 text-[11px] py-1.5 rounded-xl font-bold transition flex items-center justify-center gap-1 border ${
              graceState.active 
                ? "bg-destructive/20 hover:bg-destructive/30 text-destructive border-destructive/50" 
                : "bg-destructive/10 hover:bg-destructive/20 text-destructive border-destructive/30"
            }`}
          >
            <ShieldAlert size={12} /> 
            {graceState.active ? "⏹️ إيقاف مهلة الانتظار" : "بدء مهلة الانتظار (10 دقائق)"}
          </button>
        )}
      </div>

      {modalImage && (
        <div className="fixed inset-0 z-[999] bg-black/90 backdrop-blur-md flex items-center justify-center p-4" onClick={() => setModalImage(null)}>
          <div className="relative max-w-4xl w-full max-h-[90vh] flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setModalImage(null)} className="absolute -top-10 right-0 text-white p-2">
              <X size={20} />
            </button>
            <img src={modalImage} alt="إثبات" className="max-w-full max-h-[85vh] rounded-2xl object-contain border border-white/10" />
          </div>
        </div>
      )}
    </>
  );
}

// ==============================================================================
// 🗺️ شبكة المابات الديناميكية
// ==============================================================================
export function MapsGrid({ match, isAdmin, isLeaderA, isLeaderB, userClanId, onRefresh, onGrace, graceBusy }) {
  const getMapCount = (format) => {
    const f = (format || "").toLowerCase();
    if (f === "bo1") return 1;
    if (f === "bo5") return 5;
    if (f === "bo7") return 7;
    return 3;
  };

  const [draft, setDraft] = useState(null);
  const [savingCorrection, setSavingCorrection] = useState(false);
  const [correctionResult, setCorrectionResult] = useState(null);
  useEffect(()=>{setDraft(null);setCorrectionResult(null);},[match.id]);
  const stageCorrection = (change) => {
    setCorrectionResult(null);
    setDraft(current => ({
      operation_id: window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`,
      expected_winner_clan_id: current?.expected_winner_clan_id || match.winner_clan_id,
      expected_result_revision: current ? current.expected_result_revision : (match.result_revision || null),
      map_corrections: [...(current?.map_corrections || []).filter(c=>c.map_index!==change.map_index), change].sort((a,b)=>a.map_index-b.map_index)
    }));
  };
  const applyCorrection = async () => {
    if (!draft || savingCorrection) return;
    setSavingCorrection(true);
    try {
      const {data} = await api.post(`/matches/${match.id}/admin-correct-maps`, draft);
      setCorrectionResult(data); setDraft(null);
      window.dispatchEvent(new Event("rivals:match-result-changed"));
      await onRefresh?.(true);
      toast.success("تم تصحيح المابات والنقاط للطرفين");
    } catch(err) {toast.error(formatApiErrorDetail(err.response?.data?.detail) || "تعذر التصحيح؛ لم يتم اعتماد الطلب");}
    finally {setSavingCorrection(false);}
  };
  const previewMaps = (match.maps || []).map((mp,index)=>{
    const change=draft?.map_corrections.find(c=>c.map_index===index);
    return change ? {...mp,winner:change.winner_clan_id===match.clan_a_id ? "A" : "B"} : mp;
  });
  const mapCount = match?.maps?.length || getMapCount(match?.match_format);
  const mapsList = match?.maps || [];
  
  const activeMapIndex = mapsList.findIndex(m => !m.winner && !m.winner_clan_id && !match[`map_${mapsList.indexOf(m) + 1}_winner_clan_id`]);
  const activeMapNumber = activeMapIndex === -1 ? 1 : activeMapIndex + 1;

  return (
    <><div className="grid grid-cols-1 md:grid-cols-3 gap-3 my-4">
      {Array.from({ length: mapCount }).map((_, index) => (
        <MapCard
          key={index + 1}
          mapNumber={index + 1}
          match={match}
          isAdmin={isAdmin}
          isLeaderA={isLeaderA}
          isLeaderB={isLeaderB}
          userClanId={userClanId}
          onRefresh={onRefresh}
          onGrace={onGrace}
          graceBusy={graceBusy}
          activeMapNumber={activeMapNumber}
          onStageCorrection={stageCorrection}
          stagedCorrection={draft?.map_corrections.find(c=>c.map_index===index)}
          correctionEnabled={!match.tournament_id && !savingCorrection && !["A","B"].some(side=>previewMaps.slice(0,index).filter(m=>m.winner===side).length>=Math.floor(mapCount/2)+1)}
        />
      ))}
    </div>
    {draft && <div className="rounded-xl border border-gold-500/30 p-4 space-y-3" data-testid="map-correction-draft">
      <p className="text-sm">مابات التصحيح: {draft.map_corrections.map(c=>c.map_index+1).join("، ")}. أضف كل المابات اللازمة لحسم النتيجة؛ لن تتغير المابات الأخرى.</p>
      <button disabled={savingCorrection} onClick={applyCorrection} className="bg-gold-500 text-black rounded-lg px-4 py-2 font-bold">{savingCorrection ? "جارٍ الاعتماد..." : "اعتماد التصحيح والنقاط"}</button>
      <button disabled={savingCorrection} onClick={()=>setDraft(null)} className="mr-3 text-white/60">إلغاء التصحيح</button>
    </div>}
    {correctionResult && <div className="rounded-xl border border-emerald-500/30 p-4 mt-3" data-testid="map-correction-result">
      {correctionResult.clans?.map(row=><p key={row.id}>{row.id===match.clan_a_id ? match.clan_a?.name : match.clan_b?.name}: {row.before} → {row.after} RR ({row.delta>0 ? "+" : ""}{row.delta})</p>)}
    </div>}
    </>
  );
}

export function MatchHeader({ match, wonA, wonB, isLeaderA, isLeaderB, onDispute, onWithdraw }) {
  const formatLabel = (match.match_format || "bo3").toUpperCase();

  return (
    <div className="bg-[#111113]/90 border border-white/10 rounded-2xl p-6 md:p-8 shadow-2xl relative overflow-hidden">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          {match.status === "live" ? (
            <>
              <span className="live-dot" />
              <span className="text-xs uppercase tracking-widest text-destructive font-black px-2.5 py-0.5 rounded-full border border-destructive/30 bg-destructive/10">مباشر</span>
            </>
          ) : (
            <span className="text-xs font-bold uppercase tracking-widest text-white/40 bg-white/5 px-2.5 py-0.5 rounded-full border border-white/10">
              {match.withdrawn_clan_id ? "انسحاب" : "منتهية"}
            </span>
          )}
          <span className="text-xs text-gold-400">{match.tournament_id ? <Link to={`/tournaments/${match.tournament_id}`}>مباراة بطولة • الشجرة</Link> : match.league_id ? "مباراة دوري" : "مباراة كلانات"}</span>
          <span className="text-xs text-white/40 font-mono">| {match.game || "Call of Duty"} • {formatLabel}</span>
        </div>
        {match.status === "live" && (isLeaderA || isLeaderB) && (
          <div className="flex gap-2 flex-wrap">
            <button data-testid="withdraw-btn" onClick={onWithdraw} className="px-3 py-1.5 rounded-xl border border-destructive/40 text-destructive text-xs hover:bg-destructive/10 font-bold transition flex items-center gap-1">
              <LogOut size={13} /> {match.tournament_id ? "انسحاب من البطولة" : "انسحاب (-3)"}
            </button>
            <button data-testid="dispute-btn" onClick={onDispute} className="px-3 py-1.5 rounded-xl border border-destructive/40 text-destructive text-xs hover:bg-destructive/10 font-bold transition flex items-center gap-1">
              <Flag size={13} /> نزاع (طلب منظم)
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-4 md:gap-8" dir="ltr">
        <Link to={`/clans/${match.clan_a?.id}`} className="text-right group">
          <div className="text-xs text-gold-500 font-mono font-bold">[{match.clan_a?.tag}]</div>
          <div className="font-display font-black text-2xl md:text-4xl text-white group-hover:text-gold-400 transition">{match.clan_a?.name}</div>
          {match.withdrawn_clan_id === match.clan_a?.id && (
            <div className="text-[10px] uppercase font-bold text-destructive mt-1">منسحب</div>
          )}
        </Link>
        <div className="text-center">
          <div className="font-display font-black text-4xl md:text-6xl text-white" dir="ltr">
            <span className={match.winner_clan_id === match.clan_a_id ? "text-gold-500" : ""}>{match.status === "finished" ? match.score_a : wonA}</span>
            <span className="text-white/20 mx-3">-</span>
            <span className={match.winner_clan_id === match.clan_b_id ? "text-gold-500" : ""}>{match.status === "finished" ? match.score_b : wonB}</span>
          </div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-white/40 mt-1">{formatLabel}</div>
        </div>
        <Link to={`/clans/${match.clan_b?.id}`} className="text-left group">
          <div className="text-xs text-gold-500 font-mono font-bold">[{match.clan_b?.tag}]</div>
          <div className="font-display font-black text-2xl md:text-4xl text-white group-hover:text-gold-400 transition">{match.clan_b?.name}</div>
          {match.withdrawn_clan_id === match.clan_b?.id && (
            <div className="text-[10px] uppercase font-bold text-destructive mt-1">منسحب</div>
          )}
        </Link>
      </div>
    </div>
  );
}

function ChatComposer({ text, image, video, videoProgress, isPlus, onText, onImage, onVideo, onClearImage, onClearVideo, onSubmit }) {
  const maxMB = isPlus ? 500 : 80;
  return (
    <form onSubmit={onSubmit} className="border-t border-white/10 p-3.5 space-y-2 bg-black/40" data-testid="chat-form">
      {videoProgress > 0 && (
        <div className="bg-black/60 border border-white/10 rounded-xl p-2.5">
          <div className="flex items-center justify-between text-xs text-white/60 mb-1">
            <span>جارٍ رفع الفيديو...</span>
            <span className="font-mono font-bold text-gold-500">{videoProgress}%</span>
          </div>
          <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
            <div className="h-full bg-gold-500 transition-all duration-200" style={{ width: `${videoProgress}%` }} />
          </div>
        </div>
      )}
      {(image || video) && (
        <div className="flex gap-2">
          {image && (
            <div className="relative">
              <img src={image} alt="" className="h-16 w-16 rounded-xl object-cover border border-white/20" />
              <button type="button" onClick={onClearImage} className="absolute -top-1.5 -right-1.5 bg-destructive rounded-full text-white text-xs w-5 h-5 grid place-items-center shadow">×</button>
            </div>
          )}
          {video && (
            <div className="relative">
              <div className="h-16 w-16 rounded-xl border border-white/20 bg-black/60 grid place-items-center text-gold-500">
                <Video size={20} />
              </div>
              <button type="button" onClick={onClearVideo} className="absolute -top-1.5 -right-1.5 bg-destructive rounded-full text-white text-xs w-5 h-5 grid place-items-center shadow">×</button>
            </div>
          )}
        </div>
      )}
      <div className="flex gap-2 items-center">
        <label className="cursor-pointer p-2 rounded-xl hover:bg-white/10 text-white/60 transition" title="صورة">
          <ImageIcon size={20} />
          <input data-testid="chat-image-input" type="file" accept="image/*" onChange={onImage} className="hidden" />
        </label>
        <label className="cursor-pointer p-2 rounded-xl hover:bg-white/10 text-white/60 transition" title={`فيديو (${maxMB}MB كحد أقصى)`}>
          <Video size={20} />
          <input data-testid="chat-video-input" type="file" accept="video/*" onChange={onVideo} className="hidden" disabled={videoProgress > 0} />
        </label>
        <input
          data-testid="chat-input"
          value={text}
          onChange={(e) => onText(e.target.value)}
          placeholder="اكتب رسالة..."
          className="flex-1 bg-black/50 border border-white/10 rounded-xl px-4 py-2.5 outline-none focus:border-gold-500/50 text-sm text-white placeholder:text-white/30 transition"
        />
        <button data-testid="send-chat" type="submit" disabled={videoProgress > 0} className="px-4 py-2.5 rounded-xl bg-gold-500 text-black font-black hover:bg-gold-400 transition disabled:opacity-50 text-xs shadow-md">
          <Send size={16} />
        </button>
      </div>
    </form>
  );
}

const ChatMessagesList = memo(function ChatMessagesList({ messages, user, isAdmin, userClanId, onOpponentDecide, onAdminDecide, matchClans }) {
  return (
    <>
      {messages.length === 0 && (
        <div className="text-center text-white/40 py-12 text-xs">لا توجد رسائل بعد</div>
      )}
      {messages.map((m) => (
        <ChatMessage
          key={m.id}
          m={m}
          user={user}
          isAdmin={isAdmin}
          userClanId={userClanId}
          onOpponentDecide={onOpponentDecide}
          onAdminDecide={onAdminDecide}
          matchClans={matchClans}
        />
      ))}
    </>
  );
});

export function MvpVoteModal({ status, onVote }) {
  if (!status?.can_vote) return null;
  return (
    <div className="my-4 flex justify-center" data-testid="mvp-vote-modal">
      <div className="w-full max-w-md rounded-2xl border border-white/15 bg-[#141417] p-6 space-y-4 shadow-2xl">
        <h3 className="font-display font-black text-xl text-gold-500 flex items-center gap-2">
          <Swords size={20} /> Rivals MVP • تصويت نجم المباراة
        </h3>
        <p className="text-xs text-white/60 leading-relaxed">اختر نجم المباراة من زملائك المحضرين (لا يمكنك التصويت لنفسك).</p>
        <div className="space-y-2 max-h-60 overflow-y-auto">
          {(status.eligible_teammates || []).map((p) => (
            <button
              key={p.id}
              onClick={() => onVote(p.id)}
              className="w-full text-right rounded-xl border border-white/10 bg-black/40 px-4 py-2.5 hover:border-gold-500/50 hover:bg-gold-500/10 transition"
            >
              <div className="font-bold text-sm text-white">{p.username}</div>
              <div className="text-[10px] text-white/45">{p.act || "—"}</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function TacticalPrayerBanner({ banner }) {
  if (!banner?.message) return null;
  const remaining = banner?.window_ends_at ? new Date(banner.window_ends_at) - new Date() : 0;
  if (remaining <= 0) return null;
  const mins = Math.max(1, Math.ceil(remaining / 60000));
  return (
    <div data-testid="tactical-prayer-banner" className="mb-3 rounded-2xl border border-emerald-500/35 bg-emerald-500/10 p-4 shadow-lg">
      <div className="text-[10px] uppercase font-bold tracking-widest text-emerald-400 mb-1">تنبيه أذان الصلاة</div>
      <p className="text-xs text-emerald-100 leading-relaxed font-bold">{banner.message}</p>
      <div className="mt-1.5 text-[11px] text-emerald-300/80">نافذة التفعيل متاحة الآن — متبقي تقريباً {mins} دقيقة</div>
    </div>
  );
}

export default function MatchDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const isManagementUser = user?.role === "owner" || user?.role === "admin";
  const [match, setMatch] = useState(null);
  const [messages, setMessages] = useState([]);
  const [canWrite, setCanWrite] = useState(false);
  const [isAdminFlag, setIsAdminFlag] = useState(false);
  const [userClanInChat, setUserClanInChat] = useState(null);
  const [text, setText] = useState("");
  const [image, setImage] = useState(null);
  const [video, setVideo] = useState(null);
  const [videoProgress, setVideoProgress] = useState(0);
  const [mvpStatus, setMvpStatus] = useState(null);
  const [tacticalBanner, setTacticalBanner] = useState(null);
  const [guardStatus, setGuardStatus] = useState(null);
  const lastAlertKeyRef = useRef("");
  const scrollRef = useRef(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const wasNearBottomRef = useRef(true);

  const matchRequestRef = useRef(null);
  const matchVersionRef = useRef(0);
  const graceBusyRef = useRef(false);
  const [graceBusy, setGraceBusy] = useState(false);
  useEffect(() => () => {
    matchVersionRef.current += 1;
    matchRequestRef.current?.controller.abort();
    matchRequestRef.current = null;
  }, [id]);

  const loadMatch = useCallback((force = false) => {
    if (force === true) {matchVersionRef.current += 1;matchRequestRef.current?.controller.abort();matchRequestRef.current = null;}
    if (matchRequestRef.current) return matchRequestRef.current.promise;
    const controller = new AbortController();
    const version = matchVersionRef.current;
    const pending = { controller, promise: null };
    pending.promise = api.get(`/matches/${id}`, { signal: controller.signal, timeout: 10000 })
      .then(({ data }) => {
        if (!controller.signal.aborted && version === matchVersionRef.current) {
          setMatch(previous => matchWithClock(data, previous?.serverClockOffsetMs));
        }
      })
      .catch(() => { /* The next poll retries without stacking requests. */ })
      .finally(() => {
        if (matchRequestRef.current === pending) matchRequestRef.current = null;
      });
    matchRequestRef.current = pending;
    return pending.promise;
  }, [id]);

  const chatRequestRef = useRef(null);
  useEffect(() => () => {
    chatRequestRef.current?.controller.abort();
    chatRequestRef.current = null;
  }, [id, isManagementUser, user?.clan_id]);
  const loadChat = useCallback(() => {
    if (chatRequestRef.current) return chatRequestRef.current.promise;
    const controller = new AbortController();
    const request = { controller, promise: null };
    request.promise = api.get(`/matches/${id}/chat`, { signal: controller.signal, timeout: 10000 })
      .then(({ data }) => {
        if (controller.signal.aborted) return;
        setMessages(data.messages);
        setCanWrite(data.can_write);
        setIsAdminFlag(Boolean(data.is_admin || isManagementUser));
        setUserClanInChat(data.user_clan_id || user?.clan_id || null);
        setTacticalBanner(data.tactical_banner || null);
      })
      .catch(() => { /* Keep the last successful state and retry on the next poll. */ })
      .finally(() => { if (chatRequestRef.current === request) chatRequestRef.current = null; });
    chatRequestRef.current = request;
    return request.promise;
  }, [id, isManagementUser, user?.clan_id]);

  useEffect(() => {
    if (!tacticalBanner?.issued_at) return;
    if (lastAlertKeyRef.current === tacticalBanner.issued_at) return;
    lastAlertKeyRef.current = tacticalBanner.issued_at;
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = 528;
      gain.gain.setValueAtTime(0.0001, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.03, ctx.currentTime + 0.08);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 1.4);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 1.45);
      setTimeout(() => ctx.close(), 1800);
    } catch {
      // fallback
    }
  }, [tacticalBanner?.issued_at]);

  const loadMvpStatus = useCallback(async () => {
    try {
      const { data } = await api.get(`/matches/${id}/mvp-status`, { timeout: 5000 });
      setMvpStatus(data);
    } catch {
      setMvpStatus(null);
    }
  }, [id]);

  useEffect(() => {
    loadMatch();
    loadChat();

    const chatTick = () => {
      if (document.visibilityState === "visible") loadChat();
    };
    const matchTick = () => {
      if (document.visibilityState === "visible") loadMatch();
    };

    const chatTimer = setInterval(chatTick, CHAT_POLL_MS);
    const matchTimer = setInterval(matchTick, match?.tournament_id ? 3000 : MATCH_POLL_MS);

    return () => {
      clearInterval(chatTimer);
      clearInterval(matchTimer);
    };
  }, [loadMatch, loadChat, match?.tournament_id]);

  useEffect(() => {
    if (match?.status === "finished" && user) {
      loadMvpStatus();
      const timer = setInterval(loadMvpStatus, 12000);
      return () => clearInterval(timer);
    }
  }, [match?.status, user, loadMvpStatus]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (wasNearBottomRef.current) {
      el.scrollTop = el.scrollHeight;
      setUnreadCount(0);
    } else if (messages.length) {
      setUnreadCount((n) => n + 1);
    }
  }, [messages.length]);

  const handleChatScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    wasNearBottomRef.current = nearBottom;
    if (nearBottom) setUnreadCount(0);
  }, []);

  const jumpToLatest = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    wasNearBottomRef.current = true;
    setUnreadCount(0);
  }, []);

  const matchClans = useMemo(
    () => ({ a: match?.clan_a?.id, b: match?.clan_b?.id }),
    [match?.clan_a?.id, match?.clan_b?.id]
  );

  const wonA = useMemo(
    () => (match?.maps?.filter((m) => m.winner === "A" || m.winner_clan_id === match.clan_a_id).length ?? 0),
    [match?.maps, match?.clan_a_id]
  );
  const wonB = useMemo(
    () => (match?.maps?.filter((m) => m.winner === "B" || m.winner_clan_id === match.clan_b_id).length ?? 0),
    [match?.maps, match?.clan_b_id]
  );

  const isLeaderA = !!(user && match?.clan_a && user.clan_id === match.clan_a.id && (user.id === match.clan_a.leader_id || match.clan_a.vice_leader_ids?.includes(user.id)));
  const isLeaderB = !!(user && match?.clan_b && user.clan_id === match.clan_b.id && (user.id === match.clan_b.leader_id || match.clan_b.vice_leader_ids?.includes(user.id)));

  const onImage = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > IMG_MAX) return toast.error("الصورة كبيرة (الحد 3MB)");
    setImage(await readAsDataURL(f));
  };
  const onVideo = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const maxMB = user?.is_plus ? 500 : 80;
    if (f.size > maxMB * 1024 * 1024) {
      return toast.error(`الفيديو كبير. الحد ${maxMB}MB` + (user?.is_plus ? "" : " — ترقى لـ Plus لزيادتها لـ 500MB"));
    }
    setVideoProgress(1);
    try {
      const url = await uploadVideo(f, setVideoProgress);
      setVideo({ url, name: f.name, size: f.size });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "فشل رفع الفيديو");
    } finally {
      setVideoProgress(0);
    }
  };

  const handleErr = useCallback((err) => {
    toast.error(formatApiErrorDetail(err.response?.data?.detail));
  }, []);

  const send = (e) => {
    e.preventDefault();
    const rawText = text.trim();
    if (!rawText && !image && !video) return;

    const draftImage = image;
    const draftVideo = video;
    const tempId = `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const currentSafeClanId = userClanInChat || user?.clan_id || null;
    const optimistic = {
      id: tempId,
      match_id: id,
      user_id: user?.id || "",
      username: user?.username || "أنت",
      user_clan_id: user?.clan_id || currentSafeClanId || "",
      user_role: isAdminFlag ? "admin" : "vice",
      text: rawText,
      image: draftImage,
      video: draftVideo?.url || "",
      type: draftVideo?.url ? "video" : (draftImage ? "image" : "text"),
      created_at: new Date().toISOString(),
      _optimistic: true,
      _sendError: false,
    };

    setMessages((prev) => [...prev, optimistic]);
    setText("");
    setImage(null);
    setVideo(null);

    api.post(`/matches/${id}/chat`, {
      text: rawText,
      image: draftImage,
      video: draftVideo?.url || null,
    })
      .then(() => {
        setMessages((prev) => prev.map((m) => (m.id === tempId ? { ...m, _optimistic: false } : m)));
        loadChat();
      })
      .catch((err) => {
        setMessages((prev) => prev.map((m) => (m.id === tempId ? { ...m, _optimistic: false, _sendError: true } : m)));
        handleErr(err);
      });
  };

  const dispute = async () => {
    try {
      await api.post(`/matches/${id}/dispute`);
      toast.success("تم استدعاء المنظم");
      loadMatch();
    } catch (err) { handleErr(err); }
  };

  // Start/cancel are explicit operations; display the state returned by the server.
  const startGrace = async (mapNumber) => {
    if (graceBusyRef.current) return;
    graceBusyRef.current = true;
    setGraceBusy(true);
    const version = ++matchVersionRef.current;
    matchRequestRef.current?.controller.abort();
    matchRequestRef.current = null;
    try {
      const mapIndex = mapNumber - 1;
      const isGraceActive = Boolean(match?.maps?.[mapIndex]?.grace_state?.active);
      const suffix = isGraceActive ? "/cancel" : "";
      const { data } = await api.post(`/matches/${id}/maps/${mapIndex}/grace${suffix}`, null, { timeout: 10000 });
      if (version === matchVersionRef.current) {
        matchVersionRef.current += 1;
        setMatch(previous => matchWithClock(data, previous?.serverClockOffsetMs));
        toast.success(isGraceActive ? "تم إيقاف مهلة الانتظار" : "بدأت مهلة 10 دقائق");
      }
    } catch (err) {
      if (version === matchVersionRef.current) {
        matchVersionRef.current += 1;
        handleErr(err);
        matchRequestRef.current?.controller.abort();
        matchRequestRef.current = null;
        loadMatch();
      }
    } finally {
      graceBusyRef.current = false;
      setGraceBusy(false);
    }
  };

  const withdraw = async () => {
    if (!confirm(match.tournament_id ? "الانسحاب يمنح الخصم الفوز والتأهل في البطولة. هل تؤكد؟" : "هل أنت متأكد؟ سيتم خصم 3 نقاط من كلانك وفوز الخصم.")) return;
    try {
      await api.post(`/matches/${id}/withdraw`);
      toast.success("تم الانسحاب من المباراة");
      loadMatch();
      loadChat();
    } catch (err) { handleErr(err); }
  };

  const opponentDecide = useCallback(async (msgId, decision) => {
    try {
      await api.post(`/chat/${msgId}/opponent-decision`, { decision });
      toast.success(decision === "accept" ? "تم التأكيد" : "تم الرفض، سيتدخل المنظم");
      loadChat();
    } catch (err) { handleErr(err); }
  }, [loadChat, handleErr]);

  const adminDecide = useCallback(async (msgId, decision) => {
    let note = "";
    let finalDecision = decision;
    if (decision === "reject") {
      note = prompt("سبب الرفض (سيظهر تحت الفيديو):") || "";
      if (!note.trim()) {
        toast.error("يجب كتابة سبب الرفض");
        return;
      }
    } else if (decision === "note") {
      note = prompt("اكتب ملاحظتك:") || "";
      if (!note) return;
      finalDecision = "approve";
    }
    try {
      await api.post(`/chat/${msgId}/admin-decision`, { decision: finalDecision, note });
      loadChat();
    } catch (err) { handleErr(err); }
  }, [loadChat, handleErr]);

  const voteMvp = async (playerId) => {
    try {
      const { data } = await api.post(`/matches/${id}/mvp-vote`, { player_id: playerId });
      if (data?.winner_user_id) {
        toast.success("تم إغلاق التصويت وتحديد نجم المباراة");
      } else {
        toast.success("تم تسجيل صوتك");
      }
      loadMvpStatus();
    } catch (err) { handleErr(err); }
  };

  const isStaffOfMatch = isAdminFlag || isLeaderA || isLeaderB;
  const currentSafeClanId = userClanInChat || user?.clan_id || null;

  // RivalsGuard visibility is intentionally stricter than the rest of the match page:
  // only players marked attending THIS match, plus actual platform organizers/admins.
  const isGuardAttendee = useMemo(() => {
    if (!match || !user?.id) return false;
    const attending = [
      ...(match.attendance_a_ids || []),
      ...(match.attendance_b_ids || []),
    ];
    return attending.includes(user.id);
  }, [match, user?.id]);

  const canSeeRivalsGuard = Boolean(isGuardAttendee || isAdminFlag || isManagementUser);

  if (!match) return <div className="text-white/40 text-center py-12">جارٍ التحميل...</div>;

  return (
    <div className="space-y-6" dir="rtl">
      <MatchHeader
        match={match} wonA={wonA} wonB={wonB}
        isLeaderA={isLeaderA} isLeaderB={isLeaderB}
        onDispute={dispute} onWithdraw={withdraw}
      />
      <MvpVoteModal status={mvpStatus} onVote={voteMvp} />
      
      {/* 🗺️ قسم المابات */}
      <div className="bg-[#111113]/90 border border-white/10 rounded-2xl p-5 md:p-6 shadow-xl">
        <MapsGrid
          match={match}
          isAdmin={isAdminFlag}
          isLeaderA={isLeaderA}
          isLeaderB={isLeaderB}
          userClanId={currentSafeClanId}
          onRefresh={loadMatch}
          onGrace={startGrace}
          graceBusy={graceBusy}
        />

        <PartyCodeSection match={match} matchId={id} onRefresh={loadMatch} isStaffOfMatch={isStaffOfMatch} userClanId={currentSafeClanId} />

        {(() => {
          const mapsList = match?.maps || [];
          const winsA = mapsList.filter(m => m.winner === "A" || m.winner_clan_id === match.clan_a_id).length;
          const winsB = mapsList.filter(m => m.winner === "B" || m.winner_clan_id === match.clan_b_id).length;
          
          let dynamicWinnerId = match.winner_clan_id;
          if (match.status !== "finished" && winsA >= Math.floor((match.maps?.length || ({ bo1: 1, bo3: 3, bo5: 5, bo7: 7 }[match.match_format] || 3)) / 2) + 1) dynamicWinnerId = match.clan_a_id;
          else if (match.status !== "finished" && winsB >= Math.floor((match.maps?.length || ({ bo1: 1, bo3: 3, bo5: 5, bo7: 7 }[match.match_format] || 3)) / 2) + 1) dynamicWinnerId = match.clan_b_id;

          const winningClanName = dynamicWinnerId === match.clan_a_id ? match.clan_a?.name : (dynamicWinnerId === match.clan_b_id ? match.clan_b?.name : null);

          if (!winningClanName && match.status !== "finished") return null;

          return (
            <div className="mt-4 text-center py-3 bg-gold-500/10 border border-gold-500/30 rounded-xl">
              <div className="text-[10px] uppercase font-bold tracking-widest text-gold-500">الكلان الفائز بالمباراة</div>
              <div className="font-display font-black text-2xl text-gold-500">
                {winningClanName || "قيد تحديد الفائز..."}
              </div>
            </div>
          );
        })()}

        {!!mvpStatus?.winner && (
          <div className="mt-2 text-center py-2 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-xs font-bold">
            نجم المباراة (MVP): {mvpStatus.winner.username} 🏆
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 items-start">
        
        <aside className="space-y-4 lg:sticky lg:top-20 order-2 lg:order-1">
          <H2HWidget matchId={id} />
          <LiveStreamsPanel matchId={id} />
          <CounterUAVSelectorPanel match={match} isStaffOfMatch={isStaffOfMatch} userClanId={currentSafeClanId} onRefresh={loadMatch} />
        </aside>

        <div className="lg:col-span-2 bg-[#111113]/90 border border-white/10 rounded-2xl overflow-hidden shadow-2xl flex flex-col order-1 lg:order-2">
          <div className="p-4 border-b border-white/10 flex items-center justify-between gap-2 flex-wrap bg-white/[0.02]">
            <div className="flex items-center gap-2">
              <Shield size={18} className="text-gold-500" />
              <h2 className="font-display font-black text-base text-white">شات المباراة الحي</h2>
              <span className="text-[10px] text-white/40 bg-white/5 border border-white/10 px-2 py-0.5 rounded-full">
                {isStaffOfMatch ? "نص + وسائط" : "وسائط فقط (للزوار)"}
              </span>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <MatchPrayerBreak
                match={{ ...match, tactical_banner: tacticalBanner }}
                userSide={isLeaderA ? "A" : isLeaderB ? "B" : null}
                isStaff={isAdminFlag}
                onUpdate={() => { loadMatch(); loadChat(); }}
              />
            </div>
          </div>

          {tacticalBanner?.message && (
            <div className="px-4 pt-3">
              <TacticalPrayerBanner banner={tacticalBanner} />
            </div>
          )}

          <div 
            ref={scrollRef}
            onScroll={handleChatScroll}
            className="max-h-[600px] min-h-[300px] overflow-y-auto p-4 flex flex-col gap-3" 
            data-testid="chat-messages"
          >
            <ChatMessagesList
              messages={messages}
              user={user}
              isAdmin={isAdminFlag}
              userClanId={currentSafeClanId}
              onOpponentDecide={opponentDecide}
              onAdminDecide={adminDecide}
              matchClans={matchClans}
            />
          </div>

          {unreadCount > 0 && (
            <div className="px-4 pb-2 flex justify-center">
              <button onClick={jumpToLatest} className="rounded-full bg-red-600 text-white text-xs font-black px-4 py-2 shadow-lg">↓ {unreadCount} رسائل جديدة</button>
            </div>
          )}

          {canWrite ? (
            <ChatComposer
              text={text} image={image} video={video}
              videoProgress={videoProgress}
              isPlus={!!user?.is_plus}
              onText={setText} onImage={onImage} onVideo={onVideo}
              onClearImage={() => setImage(null)}
              onClearVideo={() => setVideo(null)}
              onSubmit={send}
            />
          ) : (
            <div className="border-t border-white/10 p-4 text-center text-white/40 text-xs flex items-center justify-center gap-2 bg-black/30" data-testid="chat-readonly">
              <Lock size={14} /> الكتابة مقتصرة على القادة، النواب والمنظمين فقط
            </div>
          )}
        </div>

        <aside className="space-y-4 lg:sticky lg:top-20 order-3">
          <ZoomV2 matchId={id} />
          <details className="mt-4"><summary className="cursor-pointer text-sm text-white/60">بلاغات النظام السابق وأدواته</summary><ScopeReportPanel matchId={id} user={user} isStaff={isAdminFlag} /></details>
          {canSeeRivalsGuard && (
            <RivalsGuardPanel
              matchId={id}
              clanAName={match.clan_a?.name}
              clanBName={match.clan_b?.name}
              user={user}
              isStaff={Boolean(isAdminFlag || isManagementUser)}
              onStatusChange={setGuardStatus}
            />
          )}
        </aside>

      </div>
    </div>
  );
}
